import dayjs from 'dayjs';
import 'dayjs/locale/th';

dayjs.locale('th');

const initializePdfMake = () => {
  if (!window.pdfMake) throw new Error('ไม่พบระบบสร้าง PDF');
  window.pdfMake.fonts = {
    THSarabunNew: {
      normal: 'THSarabunNew.ttf',
      bold: 'THSarabunNew Bold.ttf',
      italics: 'THSarabunNew Italic.ttf',
      bolditalics: 'THSarabunNew BoldItalic.ttf'
    }
  };
};

const number = value => {
  const amount = Number(value || 0);
  if (Math.abs(amount) < 0.005) return '-';
  return amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const totalAmountCell = value => ({
  stack: [
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 94, y2: 0, lineWidth: 0.8 }] },
    { text: number(value), bold: true, alignment: 'right', margin: [0, 2, 0, 2] },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 94, y2: 0, lineWidth: 0.8 }] }
  ],
  margin: [0, 2, 0, 2]
});

const accountRow = row => ([
  { text: `${row.code || '-'} ${row.name || '-'}`, margin: [16, 1, 0, 1] },
  { text: '', alignment: 'center' },
  { text: number(row.amount), alignment: 'right', margin: [0, 1, 0, 1] },
  { text: number(row.prior_amount), alignment: 'right', margin: [0, 1, 0, 1] }
]);

const sectionRow = label => ([
  { text: label, colSpan: 4, bold: true, margin: [0, 9, 0, 4] }, {}, {}, {}
]);

const totalRow = (label, current, prior) => ([
  { text: label, colSpan: 2, bold: true, margin: [0, 7, 0, 2] }, {},
  totalAmountCell(current), totalAmountCell(prior)
]);

const total = (rows, type, field) => rows
  .filter(row => row.type === type)
  .reduce((sum, row) => sum + Number(row[field] || 0), 0);

const createBody = (rows, currentYear, priorYear) => {
  const assets = rows.filter(row => row.type === 'asset');
  const liabilities = rows.filter(row => row.type === 'liability');
  const equities = rows.filter(row => row.type === 'equity');
  const currentAssets = total(rows, 'asset', 'amount');
  const priorAssets = total(rows, 'asset', 'prior_amount');
  const currentLiabilities = total(rows, 'liability', 'amount');
  const priorLiabilities = total(rows, 'liability', 'prior_amount');
  const currentEquity = total(rows, 'equity', 'amount');
  const priorEquity = total(rows, 'equity', 'prior_amount');

  return [
    [
      { text: '', style: 'tableHeader' },
      { text: 'หมายเหตุ', style: 'tableHeader', alignment: 'center' },
      { text: String(currentYear), style: 'tableHeader', alignment: 'center' },
      { text: String(priorYear), style: 'tableHeader', alignment: 'center' }
    ],
    sectionRow('สินทรัพย์'),
    ...assets.map(accountRow),
    totalRow('รวมสินทรัพย์', currentAssets, priorAssets),
    [{ text: '', colSpan: 4, margin: [0, 7, 0, 0] }, {}, {}, {}],
    sectionRow('หนี้สิน'),
    ...liabilities.map(accountRow),
    totalRow('รวมหนี้สิน', currentLiabilities, priorLiabilities),
    [{ text: '', colSpan: 4, margin: [0, 7, 0, 0] }, {}, {}, {}],
    sectionRow('ส่วนของเจ้าของ'),
    ...equities.map(accountRow),
    totalRow('รวมส่วนของเจ้าของ', currentEquity, priorEquity),
    [{ text: '', colSpan: 4, margin: [0, 7, 0, 0] }, {}, {}, {}],
    totalRow('รวมหนี้สินและส่วนของเจ้าของ', currentLiabilities + currentEquity, priorLiabilities + priorEquity)
  ];
};

export const createFinancialPositionDefinition = ({ rows, company, asOfDate }) => {
  const date = dayjs(asOfDate);
  const currentYear = date.year() + 543;
  const priorYear = currentYear - 1;
  const companyName = company?.company_name || company?.name || 'ชื่อกิจการ';

  return {
    pageSize: 'A4',
    pageMargins: [28, 24, 28, 38],
    defaultStyle: { font: 'THSarabunNew', fontSize: 12.5, color: '#111111' },
    footer: (currentPage, pageCount) => ({
      text: `หน้า ${currentPage}/${pageCount}`,
      alignment: 'right',
      margin: [0, 7, 28, 0],
      fontSize: 9
    }),
    content: [
      { text: companyName, bold: true, fontSize: 17 },
      { text: 'งบแสดงฐานะทางการเงิน', bold: true, fontSize: 15 },
      { text: `ณ วันที่ ${date.format('D MMMM')} ${currentYear}`, bold: true, fontSize: 13, margin: [0, 0, 0, 4] },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 539, y2: 0, lineWidth: 1 }] },
      { text: '(หน่วย : บาท)', alignment: 'right', fontSize: 9, margin: [0, 4, 0, 1] },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ['*', 58, 100, 100],
          body: createBody(rows, currentYear, priorYear)
        },
        layout: {
          hLineWidth: index => index === 1 ? 0.8 : 0,
          hLineColor: () => '#111111',
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 6,
          paddingTop: () => 1,
          paddingBottom: () => 1
        }
      }
    ],
    styles: { tableHeader: { bold: true, fontSize: 12.5 } }
  };
};

export const generateFinancialPositionPdf = payload => {
  initializePdfMake();
  const generator = window.pdfMake.createPdf(createFinancialPositionDefinition(payload));
  return new Promise((resolve, reject) => {
    try {
      generator.getDataUrl(resolve);
    } catch (error) {
      reject(error);
    }
  });
};
